import React, { useState, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import axios from "axios";
import { BASE_URL } from "../api";
import { getApiHeaders } from "../utils/helpers";
import { fetchRealAccounts } from "../hooks/useRealAccounts";
import CustomSelect from "../component/CustomSelect";
import { useAccountBalance } from "../hooks/useAccountBalance";

// Where the deposit is credited. The value is sent to the /deposit API as "type".
const DEPOSIT_TO_PANEL = "Panel";
const DEPOSIT_TO_MT5 = "MT5";
const NOTE_MAX_LENGTH = 200;

// "Deposit To" choices. The first one is the default. The selector cards only
// show when there are 2+ options, so to bring Panel Wallet back just uncomment it.
const DEPOSIT_TO_OPTIONS = [
  // { value: DEPOSIT_TO_PANEL, title: "Panel Wallet", text: "Added to your deposit wallet", icon: "bx bx-wallet" },
  { value: DEPOSIT_TO_MT5, title: "MT5 Account", text: "Sent to a live trading account", icon: "bx bx-line-chart" },
];
const DEFAULT_DEPOSIT_TO = DEPOSIT_TO_OPTIONS[0].value;
const SHOW_DEPOSIT_TO_SELECTOR = DEPOSIT_TO_OPTIONS.length > 1;

// ─────────────────────────────────────────────────────────────
// OLD Deposit page is commented out below. New crypto deposit
// functionality (same as Apex project) is implemented above.
// ─────────────────────────────────────────────────────────────

export default function Deposit() {
  const navigate = useNavigate();
  const [coinList, setCoinList] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadingDeposit, setLoadingDeposit] = useState(false);
  const [apiError, setApiError] = useState("");

  const [amount, setAmount] = useState("");
  const [selectedNetwork, setSelectedNetwork] = useState("");
  const [selectedSymbol, setSelectedSymbol] = useState("");

  // Deposit destination: panel wallet or a live MT5 account (see DEPOSIT_TO_OPTIONS)
  const [depositTo, setDepositTo] = useState(DEFAULT_DEPOSIT_TO);
  const [mt5Account, setMt5Account] = useState("");
  const [liveAccounts, setLiveAccounts] = useState([]);
  const [loadingAccounts, setLoadingAccounts] = useState(true);
  const [note, setNote] = useState("");

  // Balance of the selected MT5 account (re-fetched whenever the account changes)
  const { balance, loadingBalance } = useAccountBalance(depositTo === DEPOSIT_TO_MT5 ? mt5Account : "");

  useEffect(() => {
    fetchCoinList();
    fetchLiveAccounts();
  }, []);

  // Live MT5 account numbers for the "MT5 Account" option
  const fetchLiveAccounts = async () => {
    try {
      const res = await fetchRealAccounts();
      const list = res.data?.code === 200 && Array.isArray(res.data.data) ? res.data.data : [];
      const accounts = list.map((acc) => String(acc));
      setLiveAccounts(accounts);
      // MT5 is selected by default: if there is only one live account, pick it
      if (DEFAULT_DEPOSIT_TO === DEPOSIT_TO_MT5 && accounts.length === 1) {
        setMt5Account((prev) => prev || accounts[0]);
      }
    } catch (error) {
      console.error("Error fetching live accounts", error);
      setLiveAccounts([]);
    } finally {
      setLoadingAccounts(false);
    }
  };

  const handleDepositToChange = (value) => {
    setDepositTo(value);
    setApiError("");
    if (value === DEPOSIT_TO_MT5) {
      // Only one live account: select it for the user
      setMt5Account((prev) => prev || (liveAccounts.length === 1 ? liveAccounts[0] : ""));
    } else {
      setMt5Account("");
    }
  };

  const fetchCoinList = async () => {
    try {
      const res = await axios.get(`${BASE_URL}/account/coin_list`, {
        headers: getApiHeaders(),
      });
      if (res.data && res.data.data) {
        const coins = res.data.data.filter(
          (coin) => coin.is_deposit_enabled && coin.is_active
        );
        setCoinList(coins);
        if (coins.length > 0) {
          const firstNetwork = coins[0].network_name;
          setSelectedNetwork(firstNetwork);
          const symbols = coins.filter((c) => c.network_name === firstNetwork);
          if (symbols.length > 0) {
            setSelectedSymbol(symbols[0].coin_symbol);
          }
        }
      }
    } catch (error) {
      console.error("Error fetching coin list", error);
    } finally {
      setLoading(false);
    }
  };

  const handleNetworkChange = (net) => {
    setSelectedNetwork(net);
    const symbols = coinList.filter((c) => c.network_name === net);
    if (symbols.length > 0) {
      setSelectedSymbol(symbols[0].coin_symbol);
    } else {
      setSelectedSymbol("");
    }
  };

  const uniqueNetworks = [...new Set(coinList.map((c) => c.network_name))];
  const selectedCoinData = coinList.find(
    (c) =>
      c.network_name === selectedNetwork && c.coin_symbol === selectedSymbol
  );

  const numVal = parseFloat(amount);
  const isValidAmount =
    selectedCoinData &&
    numVal >= selectedCoinData.min_deposit &&
    numVal <= selectedCoinData.max_deposit;

  // MT5 deposits need an account; panel deposits need nothing more
  const isDestinationValid = depositTo === DEPOSIT_TO_PANEL || Boolean(mt5Account);

  const handleDeposit = async () => {
    if (!isValidAmount || !selectedCoinData || !isDestinationValid) return;

    setLoadingDeposit(true);
    setApiError("");
    try {
      const payload = {
        amount: String(amount),
        // "coin" carries the coin_name of the selected network's coin_list row (e.g. "Binance USD")
        coin: String(selectedCoinData.coin_name),
        coin_symbol: String(selectedCoinData.coin_symbol),
        coin_type: String(selectedCoinData.network_name),
      };

      const res = await axios.post(`${BASE_URL}/before_deposit`, payload, {
        headers: getApiHeaders(),
      });

      if (res.data) {
        if (res.data.status === "error" || res.data.code === 400) {
          setApiError(res.data.msg || "Invalid request parameters.");
          return;
        }
        // Destination and note travel in the URL so the payment page can send them with the final /deposit call
        const destination = new URLSearchParams({ type: depositTo });
        if (depositTo === DEPOSIT_TO_MT5) destination.set("account", mt5Account);
        if (note.trim()) destination.set("note", note.trim());

        navigate(
          `/crypto_deposit?coin=${selectedCoinData.network_name} ${selectedCoinData.coin_symbol}&amount=${amount}&address=${res.data.address || ""}&orderno=${res.data.orderno || ""}&invoiceid=${res.data.invoiceid || ""}&${destination.toString()}`
        );
      }
    } catch (error) {
      console.error("Error calling before_deposit API", error);
      const errMsg =
        error.response?.data?.msg ||
        error.response?.data?.message ||
        "Something went wrong. Please try again.";
      setApiError(errMsg);
    } finally {
      setLoadingDeposit(false);
    }
  };

  const getCoinImage = (symbol, network) => {
    const sym = String(symbol || "").toUpperCase();
    const net = String(network || "").toUpperCase();
    if (sym === "USDT" && net === "BEP20")
      return "assets/images/deposit/Tether_BEP20_USDT.svg";
    if (sym === "USDT" && net === "TRC20")
      return "assets/images/deposit/Tether_TRC20_USDT.svg";
    return "assets/images/deposit/wallet.svg";
  };

  return (
    <>
      <div id="layout-wrapper">
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-flex align-items-center justify-content-between">
                    <div>
                      <h2 className="fw-700 mb-0">Deposit</h2>
                    </div>
                    <div className="page-title-right">
                      <button
                        className="btn btn-primary waves-effect waves-light d-flex align-items-center"
                        onClick={() => navigate("/crypto_deposit_history")}
                      >
                        <i className="bx bx-history me-2 fs-5"></i> Deposit History
                      </button>
                    </div>
                  </div>
                </div>
              </div>

              <div className="row justify-content-center mt-1">
                <div className="col-xl-5 col-lg-6 col-md-8">
                  <div className="card shadow-sm border-0 rounded-4">
                    <div className="card-body p-3 p-sm-4">
                      {/* The form shows right away; while the coin list loads, the network and
                          amount fields show placeholders instead of a full-card spinner. */}
                      {!loading && coinList.length === 0 ? (
                        <div className="text-center py-4 text-muted">
                          No payment methods available.
                        </div>
                      ) : (
                        <div>
                          <h4
                            className="fw-bold mb-3 pb-3 border-bottom pb-2"
                          >
                            Crypto Deposit
                          </h4>

                          {/* Network Selection */}
                          <div className="mb-4">
                            <label className="form-label text-muted fw-semibold">
                              Select Network
                            </label>
                            <div className="row g-3">
                              {loading && [1, 2].map((i) => (
                                <div className="col-sm-6" key={`network-skeleton-${i}`}>
                                  <div className="rounded-3 p-3 border d-flex align-items-center placeholder-glow" aria-hidden="true">
                                    <span className="placeholder rounded-circle me-3" style={{ width: 32, height: 32 }} />
                                    <span className="placeholder rounded col-5" />
                                  </div>
                                </div>
                              ))}
                              {uniqueNetworks.map((netName) => {
                                const isSelected = selectedNetwork === netName;
                                const repCoin = coinList.find(
                                  (c) => c.network_name === netName
                                );
                                return (
                                  <div className="col-sm-6" key={netName}>
                                    <div
                                      onClick={() => handleNetworkChange(netName)}
                                      className={`rounded-3 p-3 px-3 d-flex align-items-center notranslate ${isSelected ? 'bg-primary bg-opacity-10 border border-primary shadow-sm' : 'bg-transparent border'}`}
                                      translate="no"
                                      style={{
                                        cursor: "pointer",
                                        transition: "all 0.2s ease"
                                      }}
                                    >
                                      <img
                                        src={getCoinImage(repCoin?.coin_symbol, netName)}
                                        alt={netName}
                                        className="me-3 notranslate"
                                        translate="no"
                                        width="32"
                                        height="32"
                                      />
                                      <div>
                                        <div className={`fw-bold notranslate ${isSelected ? 'text-primary' : 'text-body'}`} translate="no">
                                          {netName}
                                        </div>
                                      </div>
                                    </div>
                                  </div>
                                );
                              })}
                            </div>
                          </div>

                          {/* Coin (static): only USDT is supported. The coin sent to the API still
                              comes from the selected network's coin_list row (selectedCoinData). */}
                          {(loading || selectedNetwork) && (
                            <div className="mb-4">
                              <label className="form-label text-muted fw-semibold">
                                Coin
                              </label>
                              <div className="d-flex flex-wrap gap-3">
                                <div
                                  className="rounded-3 p-2 px-3 d-flex flex-column align-items-center justify-content-center text-center notranslate bg-primary bg-opacity-10 border border-primary shadow-sm"
                                  translate="no"
                                  style={{ minWidth: "125px" }}
                                >
                                  <div
                                    className="fw-bold notranslate text-primary"
                                    translate="no"
                                    style={{ fontSize: "1rem", lineHeight: "1.2" }}
                                  >
                                    USDT
                                  </div>
                                  <span
                                    className="mt-1 notranslate text-primary text-opacity-75"
                                    translate="no"
                                    style={{ fontSize: "0.78rem", fontWeight: "500", lineHeight: "1.2" }}
                                  >
                                    Tether USD
                                  </span>
                                </div>
                              </div>
                            </div>
                          )}

                          {/* Deposit destination: panel wallet or a live MT5 account.
                              The "Deposit To" cards only show when DEPOSIT_TO_OPTIONS has 2+ options. */}
                          {(loading || selectedCoinData) && (
                            <div className="mb-4">
                              {SHOW_DEPOSIT_TO_SELECTOR && (
                              <>
                              <label className="form-label text-muted fw-semibold">
                                Deposit To
                              </label>
                              <div className="row g-3">
                                {DEPOSIT_TO_OPTIONS.map((option) => {
                                  const isSelected = depositTo === option.value;
                                  const isDisabled = option.value === DEPOSIT_TO_MT5 && !loadingAccounts && liveAccounts.length === 0;
                                  return (
                                    <div className="col-sm-6" key={option.value}>
                                      <div
                                        role="radio"
                                        aria-checked={isSelected}
                                        aria-disabled={isDisabled}
                                        tabIndex={isDisabled ? -1 : 0}
                                        onClick={() => !isDisabled && handleDepositToChange(option.value)}
                                        onKeyDown={(e) => {
                                          if (!isDisabled && (e.key === "Enter" || e.key === " ")) {
                                            e.preventDefault();
                                            handleDepositToChange(option.value);
                                          }
                                        }}
                                        className={`rounded-3 p-3 d-flex align-items-center h-100 ${isSelected ? "bg-primary bg-opacity-10 border border-primary shadow-sm" : "bg-transparent border"}`}
                                        style={{
                                          cursor: isDisabled ? "not-allowed" : "pointer",
                                          opacity: isDisabled ? 0.55 : 1,
                                          transition: "all 0.2s ease",
                                        }}
                                      >
                                        <i className={`${option.icon} me-3 ${isSelected ? "text-primary" : "text-muted"}`} style={{ fontSize: "26px" }} />
                                        <div>
                                          <div className={`fw-bold ${isSelected ? "text-primary" : "text-body"}`}>{option.title}</div>
                                          <small className="text-muted">
                                            {isDisabled ? "No live account yet" : option.text}
                                          </small>
                                        </div>
                                      </div>
                                    </div>
                                  );
                                })}
                              </div>
                              </>
                              )}

                              {/* Live account picker, only for MT5 deposits */}
                              {depositTo === DEPOSIT_TO_MT5 && (
                                <div className={SHOW_DEPOSIT_TO_SELECTOR ? "mt-3" : ""}>
                                  <label className="form-label text-muted fw-semibold">
                                    MT5 Account <span className="text-danger">*</span>
                                  </label>
                                  <div className="notranslate" translate="no">
                                    <CustomSelect
                                      id="mt5_account"
                                      name="mt5_account"
                                      value={mt5Account}
                                      options={liveAccounts}
                                      onChange={(e) => {
                                        setMt5Account(String(e.target.value));
                                        setApiError("");
                                      }}
                                      placeholder={loadingAccounts ? "Loading accounts..." : "Select live account"}
                                      loading={loadingAccounts}
                                      disabled={loadingDeposit}
                                      searchable={liveAccounts.length > 8}
                                      searchPlaceholder="Search account..."
                                    />
                                  </div>
                                  {mt5Account && (
                                    <div
                                      className="d-flex align-items-center justify-content-between rounded-3 px-3 py-2 mt-2 bg-primary bg-opacity-10"
                                    >
                                      <span className="text-muted fw-medium">
                                        <i className="bx bx-wallet me-1"></i> Available Balance
                                      </span>
                                      <span className="fw-bold text-primary notranslate" translate="no">
                                        {loadingBalance
                                          ? <span className="spinner-border spinner-border-sm" role="status" aria-label="Loading balance" style={{ width: "14px", height: "14px", borderWidth: "2px" }} />
                                          : balance !== null && !isNaN(Number(balance))
                                            ? `$${Number(balance).toFixed(2)}`
                                            : "--"}
                                      </span>
                                    </div>
                                  )}
                                  {!mt5Account && !loadingAccounts && (
                                    <small className="text-muted mt-1 d-block">
                                      {liveAccounts.length === 0
                                        ? "You don't have a live MT5 account yet. Please open one to deposit."
                                        : "Select the live account that should receive this deposit."}
                                    </small>
                                  )}
                                </div>
                              )}
                            </div>
                          )}

                          {/* Amount input (placeholder until min/max limits load) */}
                          {loading && (
                            <div className="mb-4" aria-hidden="true">
                              <label className="form-label text-muted fw-semibold">
                                Amount
                              </label>
                              <div className="placeholder-glow">
                                <span className="placeholder col-12 rounded" style={{ height: 40 }} />
                                <span className="placeholder col-6 rounded mt-2" style={{ height: 12 }} />
                              </div>
                            </div>
                          )}
                          {selectedCoinData && (
                            <div className="mb-4">
                              <label className="form-label text-muted fw-semibold">
                                Amount
                              </label>
                              <div className="input-group">
                                <input
                                  type="text"
                                  inputMode="decimal"
                                  className="form-control px-3 py-2"
                                  placeholder={`Min Deposit: $${selectedCoinData.min_deposit}`}
                                  value={amount}
                                  style={{ borderRadius: "8px 0 0 8px" }}
                                  onKeyDown={(e) => {
                                    if (["e", "E", "+", "-"].includes(e.key)) {
                                      e.preventDefault();
                                    }
                                  }}
                                  onChange={(e) => {
                                    const val = e.target.value;
                                    if (val === "") {
                                      setAmount("");
                                      setApiError("");
                                      return;
                                    }
                                    if (
                                      val.includes("-") ||
                                      val.toLowerCase().includes("e")
                                    ) {
                                      return;
                                    }
                                    const num = parseFloat(val);
                                    if (isNaN(num) || num < 0) return;
                                    if (
                                      selectedCoinData &&
                                      num > selectedCoinData.max_deposit
                                    ) {
                                      return;
                                    }
                                    setAmount(val);
                                    setApiError("");
                                  }}
                                />
                                <span className="input-group-text fw-bold notranslate" translate="no" style={{ borderRadius: "0 8px 8px 0" }}>USD</span>
                              </div>
                              <small className="text-muted mt-1 d-block">
                                Min Deposit: ${selectedCoinData.min_deposit} &nbsp;|&nbsp; Max Deposit: ${selectedCoinData.max_deposit}
                              </small>
                            </div>
                          )}

                          {/* Note (optional) */}
                          {/* {selectedCoinData && (
                            <div className="mb-4">
                              <label htmlFor="deposit_note" className="form-label text-muted fw-semibold">
                                Note <span className="fw-normal">(optional)</span>
                              </label>
                              <textarea
                                id="deposit_note"
                                className="form-control px-3 py-2"
                                rows={2}
                                maxLength={NOTE_MAX_LENGTH}
                                placeholder="Add a note for this deposit"
                                value={note}
                                onChange={(e) => setNote(e.target.value)}
                                disabled={loadingDeposit}
                                style={{ resize: "none" }}
                              />
                              <small className="text-muted mt-1 d-block text-end">
                                {note.length}/{NOTE_MAX_LENGTH}
                              </small>
                            </div>
                          )} */}

                          {/* API error */}
                          {apiError && (
                            <div className="alert alert-danger py-2 rounded-3 d-flex align-items-center gap-2 mb-4">
                              <i className="bx bx-info-circle fs-5"></i>
                              <span>{apiError}</span>
                            </div>
                          )}

                          {/* Info notice */}
                          <div className="mb-4">
                            <small className="text-muted mt-1 d-block">
                              <i className="bx bx-info-circle me-1"></i>
                              Please ensure you send only <strong className="notranslate" translate="no">USDT</strong> to your deposit address. Assets sent to the wrong network cannot be recovered.
                            </small>
                          </div>

                          {/* Submit */}
                          <div className="text-end mt-4">
                            <button
                              className="btn btn-primary px-4 py-2 fw-medium"
                              onClick={handleDeposit}
                              disabled={!isValidAmount || !isDestinationValid || loadingDeposit}
                            >
                              {loadingDeposit ? (
                                <span>
                                  <span
                                    className="spinner-border spinner-border-sm me-2"
                                    role="status"
                                    aria-hidden="true"
                                  ></span>
                                  Processing...
                                </span>
                              ) : (
                                <span>
                                  Submit Deposit
                                </span>
                              )}
                            </button>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </div>
            {/* container-fluid */}
          </div>
          {/* End Page-content */}
          <Footer />
        </div>
      </div>
    </>
  );
}

/* ─────────────────────────────────────────────────────────────
   OLD DEPOSIT PAGE (commented out)
   ─────────────────────────────────────────────────────────────

import React from "react";
import { Link } from "react-router-dom";
import Sidebar from "../component/Sidebar";
import Topbar from "../component/Topbar";
import Footer from "../component/Footer";
import CommonTable from "../component/CommonTable";

export default function Deposit() {
  return (
    <>
      <div id="layout-wrapper">
        <Topbar />
        <Sidebar />
        <div className="main-content">
          <div className="page-content">
            <div className="container-fluid">
              <div className="row">
                <div className="col-12">
                  <div className="page-title-box d-sm-flex align-items-center justify-content-between">
                    <div>
                      <h2 className="fw-700 mb-sm-0">Deposit</h2>
                      <p className="text-muted mb-0 mt-2">Select your preferred payment method.</p>
                    </div>
                  </div>
                </div>
              </div>
              <div className="row">
                <div className="col-12">
                  <div className="card">
                    <div className="card-body">
                      <div className="row">
                        <div className="col-lg-12">
                          <div className="card-body">
                            <div className="table-responsive">
                              <CommonTable
                                columns={[
                                  { header: "Payment Method", ... },
                                  { header: "Currency", ... },
                                  { header: "Fees/Commission(%)", ... },
                                  { header: "Processing Time", ... },
                                  { header: "Action", ... },
                                ]}
                                data={[
                                  { image: "assets/images/deposit/Tether_BEP20_USDT.svg", title: "Tether (BEP20 USDT)", currency: "USD", commission: "0 %", processingTime: "1 Hour", link: "/deposit_tether?coin=BEP20 USDT" },
                                  { image: "assets/images/deposit/Tether_TRC20_USDT.svg", title: "Tether (TRC20 USDT)", currency: "USD", commission: "0 %", processingTime: "1 Hour", link: "/deposit_tether?coin=TRC20 USDT" },
                                  { image: "assets/images/deposit/wallet.svg", title: "Wallet", currency: "USD", commission: "0 %", processingTime: "24 Hours", link: "/deposit_wallet", disabled: true },
                                ]}
                                tableClassName="table table-nowrap align-middle table-nowrap mb-0"
                              />
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <Footer />
        </div>
      </div>
    </>
  );
}
*/
